import {
  BASE,
  BaseRouteType,
  NESTED_ROUTES,
  NestedRoutesType,
} from 'common/src/routes/BaseRoutes';
import { generateNestedRoute } from 'common/src/routes/helpers';
import { ProductFeatures } from 'common/src/types/enums/ProductFeatures.enum';
import { Products } from 'common/src/types/enums/Products.enum';

import { IAuthorizationContext } from 'portal/src/auth/IAuthorizationContext';

import { administrationRouteDefinitions } from './AdministrationRouteDefinitions';
import { billAutomationRouteDefinitions } from './BillAutomationRouteDefinitions';
import { costRouteDefinitions } from './CostRouteDefinitions';
import { evChargingRouteDefinitions } from './EvChargingRouteDefinitions';
import { reportRouteDefinitions } from './ReportRouteDefinitions';
import { RouteDefinition } from './routeGenerator';
import { smartMetersRouteDefinitions } from './SmartMetersRouteDefinitions';
import { summaryRouteDefinitions } from './SummaryRouteDefinitions';
import { systemAdministrationRouteDefinitions } from './SystemAdministrationRouteDefinitions';
import { usageRouteDefinitions } from './UsageRouteDefinitions';

import { electricRouteDefinitions } from './intervalData/ElectricRouteDefinitions';
import { naturalGasRouteDefinitions } from './intervalData/NaturalGasRouteDefinitions';
import { portfolioRouteDefinitions } from './intervalData/PortfolioRouteDefinitions';
import { waterRouteDefinitions } from './intervalData/WaterRouteDefinitions';
import useAuth from '../auth/useAuth';

export interface INavigationMenuItem {
  title: string;
  link: string;
  children?: INavigationMenuItem[];
  visible?: boolean;
  isClickable?: boolean;
  group?: string;
  dividerBefore?: boolean;
}

export interface ITopLevelNavigationItem {
  id:
    | 'dashboards'
    | 'evCharging'
    | 'reports'
    | 'smartMeters'
    | 'custom'
    | 'billAutomation'
    | 'organization'
    | 'systemAdministration';
  title: string;
  link: string;
  dividerBefore?: boolean;
  visible?: boolean;
  matchesPath: (pathname: string) => boolean;
  menuItems: INavigationMenuItem[];
}

function buildDashboardMenuItems(context: IAuthorizationContext) {
  return [
    {
      title: 'Summary',
      link: BASE.SUMMARY,
      isClickable: false,
      group: 'Summary',
      children: buildTabChildren(
        BASE.SUMMARY,
        summaryRouteDefinitions,
        context,
      ),
    },
    {
      title: 'Usage',
      link: BASE.USAGE,
      isClickable: false,
      group: 'Usage',
      children: buildTabChildren(BASE.USAGE, usageRouteDefinitions, context),
    },
    {
      title: 'Costs',
      link: BASE.COST,
      isClickable: false,
      group: 'Costs',
      children: buildTabChildren(BASE.COST, costRouteDefinitions, context),
    },
  ];
}

function buildTabChildren(
  basePath: BaseRouteType,
  definitions: RouteDefinition[],
  context: IAuthorizationContext,
): INavigationMenuItem[] {
  return definitions
    .filter(
      (definition) =>
        !definition.shouldRenderLink || definition.shouldRenderLink(context),
    )
    .map((definition) => ({
      title: definition.name,
      link: generateNestedRoute(basePath, definition.path as NestedRoutesType),
      dividerBefore: definition.dividerBefore,
    }));
}

function buildSectionMenuItems(
  title: string,
  basePath: BaseRouteType,
  definitions: RouteDefinition[],
  context: IAuthorizationContext,
): INavigationMenuItem[] {
  return [
    {
      title,
      link: basePath,
      isClickable: false,
      children: buildTabChildren(basePath, definitions, context),
    },
  ];
}

function buildIntervalDataMenuItems(context: IAuthorizationContext) {
  return [
    {
      title: 'Portfolio',
      link: BASE.INTERVAL_DATA_PORTFOLIO,
      isClickable: false,
      group: 'Portfolio',
      children: buildTabChildren(
        BASE.INTERVAL_DATA_PORTFOLIO,
        portfolioRouteDefinitions,
        context,
      ),
    },
    {
      title: 'Electric',
      link: BASE.INTERVAL_DATA_ELECTRIC,
      isClickable: false,
      group: 'Electric',
      children: buildTabChildren(
        BASE.INTERVAL_DATA_ELECTRIC,
        electricRouteDefinitions,
        context,
      ),
    },
    {
      title: 'Water',
      link: BASE.INTERVAL_DATA_WATER,
      isClickable: false,
      group: 'Water',
      children: buildTabChildren(
        BASE.INTERVAL_DATA_WATER,
        waterRouteDefinitions,
        context,
      ),
    },
    {
      title: 'Natural Gas',
      link: BASE.INTERVAL_DATA_NATURAL_GAS,
      isClickable: false,
      group: 'Natural Gas',
      children: buildTabChildren(
        BASE.INTERVAL_DATA_NATURAL_GAS,
        naturalGasRouteDefinitions,
        context,
      ),
    },
  ];
}

function buildCustomDashboardItem(
  context: IAuthorizationContext,
): ITopLevelNavigationItem {
  return {
    id: 'custom',
    title: 'Custom',
    link: BASE.CUSTOM_DASHBOARDS,
    visible: context.featureChecker.productFeatureEnabled(
      ProductFeatures.customDashboards,
    ),
    matchesPath: (pathname) => pathname.startsWith(BASE.CUSTOM_DASHBOARDS),
    menuItems: [
      {
        title: 'Custom Dashboards',
        link: BASE.CUSTOM_DASHBOARDS,
        isClickable: false,
      },
    ],
  };
}

function buildOrgDataItem(
  context: IAuthorizationContext,
): ITopLevelNavigationItem {
  return {
    id: 'organization',
    title: 'Org Data',
    link: generateNestedRoute(
      BASE.ADMINISTRATION,
      NESTED_ROUTES.ADMINISTRATION.BILLS,
    ),
    matchesPath: (pathname) => pathname.startsWith(BASE.ADMINISTRATION),
    menuItems: buildSectionMenuItems(
      'Organization',
      BASE.ADMINISTRATION,
      administrationRouteDefinitions,
      context,
    ),
  };
}

function buildSystemAdminMenuItem(
  context: IAuthorizationContext,
): ITopLevelNavigationItem {
  return {
    id: 'systemAdministration',
    title: 'System',
    link: BASE.SYSTEM_ADMINISTRATION,
    dividerBefore: true,
    visible: context.hasClientSystemAdminAccess(),
    matchesPath: (pathname) => pathname.startsWith(BASE.SYSTEM_ADMINISTRATION),
    menuItems: buildSectionMenuItems(
      'System Administration',
      BASE.SYSTEM_ADMINISTRATION,
      systemAdministrationRouteDefinitions,
      context,
    ),
  };
}

export default function useProductMenuItems(): ITopLevelNavigationItem[] {
  const {
    featureChecker,
    hasAdminAccess,
    hasClientSystemAdminAccess,
    hasReadOnlyAccess,
    hasSystemAdminAccess,
  } = useAuth();

  const context: IAuthorizationContext = {
    featureChecker,
    hasAdminAccess,
    hasClientSystemAdminAccess,
    hasReadOnlyAccess,
    hasSystemAdminAccess,
  };

  if (import.meta.env.VITE_PRODUCT_ID === Products.intervals) {
    const intervalItems: ITopLevelNavigationItem[] = [
      {
        id: 'dashboards',
        title: 'Dashboards',
        link: BASE.INTERVAL_DATA_PORTFOLIO,
        matchesPath: (pathname) =>
          [
            BASE.INTERVAL_DATA_PORTFOLIO,
            BASE.INTERVAL_DATA_ELECTRIC,
            BASE.INTERVAL_DATA_WATER,
            BASE.INTERVAL_DATA_NATURAL_GAS,
          ].some((prefix) => pathname.startsWith(prefix)),
        menuItems: buildIntervalDataMenuItems(context),
      },
      buildCustomDashboardItem(context),
      buildOrgDataItem(context),
      buildSystemAdminMenuItem(context),
    ];

    return intervalItems
      .filter((item) => item.visible !== false)
      .map((item) => ({
        ...item,
        menuItems: item.menuItems.filter(
          (subItem) => subItem.visible !== false,
        ),
      }));
  }

  const items: ITopLevelNavigationItem[] = [
    {
      id: 'dashboards',
      title: 'Dashboards',
      link: BASE.SUMMARY,
      matchesPath: (pathname) =>
        [BASE.SUMMARY, BASE.USAGE, BASE.COST].some((prefix) =>
          pathname.startsWith(prefix),
        ),
      menuItems: buildDashboardMenuItems(context),
    },
    {
      id: 'reports',
      title: 'Reports',
      link: BASE.REPORTS,
      matchesPath: (pathname) => pathname.startsWith(BASE.REPORTS),
      menuItems: buildSectionMenuItems(
        'Reports',
        BASE.REPORTS,
        reportRouteDefinitions,
        context,
      ),
    },
    buildCustomDashboardItem(context),
    buildOrgDataItem(context),
    {
      id: 'billAutomation',
      title: 'Automation',
      link: BASE.BILL_AUTOMATION,
      matchesPath: (pathname) => pathname.startsWith(BASE.BILL_AUTOMATION),
      menuItems: buildSectionMenuItems(
        'Bill Automation',
        BASE.BILL_AUTOMATION,
        billAutomationRouteDefinitions,
        context,
      ),
    },
    {
      id: 'smartMeters',
      title: 'Interval Meters',
      link: BASE.SMART_METERS,
      matchesPath: (pathname) => pathname.startsWith(BASE.SMART_METERS),
      menuItems: buildSectionMenuItems(
        'Smart Meters',
        BASE.SMART_METERS,
        smartMetersRouteDefinitions,
        context,
      ),
    },
    {
      id: 'evCharging',
      title: 'EV Charging',
      link: BASE.EV_CHARGING,
      matchesPath: (pathname) => pathname.startsWith(BASE.EV_CHARGING),
      menuItems: buildSectionMenuItems(
        'EV Charging',
        BASE.EV_CHARGING,
        evChargingRouteDefinitions,
        context,
      ),
    },
    buildSystemAdminMenuItem(context),
  ];

  return items
    .filter((item) => item.visible !== false)
    .map((item) => ({
      ...item,
      menuItems: item.menuItems
        .filter((subItem) => subItem.visible !== false)
        .map((subItem) => ({
          ...subItem,
          children: subItem.children?.filter(
            (child) => child.visible !== false,
          ),
        })),
    }));
}
